import {
	faCheck,
	faCircleExclamation,
	faClock,
	faPaperPlane,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type {
	ChatMessageView,
	ChatParticipantView,
} from '../../services/chat/chat-types';
import { Avatar } from './Avatar';

/**
 * Presentational thread + composer.
 *
 * ChatConversationPane wraps this with store / MQTT plumbing for the
 * CRM surface. The portal help widget drives it with REST polling
 * and a portal token. No internal-only state leaks into the props —
 * anything store-specific (pinning, mute, visibility, etc.) lives in
 * the pane wrapper, not here.
 *
 * Feature parity with the CRM pane:
 *   - Message grouping into 5-min windows per sender
 *   - SMS-style "mine right, theirs left" with avatars + sender name
 *     + timestamp on first bubble of a group
 *   - Centered pill for system messages
 *   - Status indicator on outgoing messages (sending / failed / sent)
 *   - Typing indicator line
 *   - Auto-grow textarea capped at maxHeight
 *   - Auto-scroll to bottom on new inbound message; scroll-preserving
 *     prepend when loading older history
 *   - "Empty thread" placeholder
 */

const GROUP_WINDOW_MS = 5 * 60_000;
const EMPTY_MESSAGES: ReadonlyArray<ChatMessageView> = [];
const EMPTY_PARTICIPANTS: ReadonlyArray<ChatParticipantView> = [];

export interface ChatThreadProps {
	messages?: ReadonlyArray<ChatMessageView>;
	participants?: ReadonlyArray<ChatParticipantView>;
	/**
	 * The viewer's own `workdUserId`. Used as the identity primitive for
	 * right-aligning the viewer's own bubbles. A single numeric compare
	 * against each message's `senderWorkdUserId` — no participants-list
	 * dependency, so alignment is correct from the moment a message
	 * lands in the store (no race against a separate participants
	 * fetch). Live-chat callers (CRM pane, ecommerce help widget) pass
	 * this. Omit for read-only audit views.
	 */
	myWorkdUserId?: number;
	/**
	 * Audit-view alignment: any participant id in this list renders
	 * its messages right-aligned, same as `myWorkdUserId` does for the
	 * viewer's own messages. Used by the archived-support transcript
	 * viewer to place ALL rep messages on the right (and customer
	 * messages on the left) — a conversation may have had multiple
	 * reps over its life, and a single id can't capture that. The
	 * viewer themself isn't a participant (they're a supervisor), so
	 * `myWorkdUserId` doesn't apply.
	 */
	rightAlignedParticipantIds?: ReadonlyArray<number>;
	/**
	 * In live chat, "mine" messages omit the sender name + avatar
	 * (those bubbles are obviously from the current user). In an
	 * audit/archive view there is no current user — the viewer is a
	 * supervisor, both sides are "them" — so suppressing the name
	 * on right-aligned bubbles makes the rep anonymous on the
	 * transcript. Set this to `true` (audit viewer does) to render
	 * the sender's name + avatar above/beside the bubble on BOTH
	 * sides, matching the way an external observer needs to read
	 * the conversation.
	 */
	showSenderInfoOnMine?: boolean;
	/**
	 * Display names of people currently typing (minus the viewer).
	 * Empty or undefined hides the indicator line.
	 */
	typingNames?: ReadonlyArray<string>;
	/**
	 * Keyed by the optimistic message's clientMessageId — indicates a
	 * failed send. Enables the retry button on the matching bubble.
	 */
	sendErrors?: Record<string, string>;
	/** Called when the composer submits. Reject to mark as failed. */
	onSend: (text: string) => void | Promise<void>;
	/** Called when the user clicks retry on a failed send bubble. */
	onRetry?: (clientMessageId: string) => void;
	/** Called when the scroll area reaches the top — load older page. */
	onLoadOlder?: () => Promise<void>;
	/** True when there's no more history to page backward through. */
	reachedStart?: boolean;
	/** True while a load-older request is in flight. */
	loadingOlder?: boolean;
	/**
	 * Visual density. `comfortable` matches the CRM `/chat` page;
	 * `compact` matches the floating widget / customer-support helper.
	 */
	density?: 'comfortable' | 'compact';
	/**
	 * Slot above the message list — e.g. "We're finding someone to
	 * help you…" banner. Rendered once at the top, inside the scroll
	 * area so it stays pinned above older history.
	 */
	banner?: React.ReactNode;
	/**
	 * Composer placeholder, e.g. "Message #sales" or "Type a
	 * message…".
	 */
	placeholder?: string;
	/** Disable the composer entirely (e.g. resolved conversation). */
	composerDisabled?: boolean;
	/**
	 * Hide the composer footer entirely. When true, the textarea +
	 * send button row is not rendered at all — pair with a
	 * `composerFooter` containing a bar like "This chat has ended"
	 * to replace it. Distinct from `composerDisabled`, which leaves
	 * the composer visible-but-greyed; "hidden" is the right call
	 * for terminal-state UIs (chat resolved, archived) where the
	 * input is never coming back. Matches the rep-side
	 * ChatConversationPane pattern.
	 */
	composerHidden?: boolean;
	/** Slot shown between the typing indicator and the composer. */
	composerLeading?: React.ReactNode;
	/** Slot shown under the composer (e.g. "End chat" link). */
	composerFooter?: React.ReactNode;
}

export const ChatThread: React.FC<ChatThreadProps> = ({
	messages = EMPTY_MESSAGES,
	participants = EMPTY_PARTICIPANTS,
	myWorkdUserId,
	rightAlignedParticipantIds,
	showSenderInfoOnMine,
	typingNames,
	sendErrors,
	onSend,
	onRetry,
	onLoadOlder,
	reachedStart,
	loadingOlder,
	density = 'comfortable',
	banner,
	placeholder,
	composerDisabled,
	composerHidden,
	composerLeading,
	composerFooter,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const compact = density === 'compact';
	const composerMaxHeight = compact ? 120 : 180;

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const composerRef = useRef<HTMLTextAreaElement | null>(null);
	const [composer, setComposer] = useState('');
	const lastAutoScrollAt = useRef(0);

	const participantsById = useMemo(() => {
		const map = new Map<number, ChatParticipantView>();
		for (const p of participants) map.set(p.id, p);
		return map;
	}, [participants]);

	const groups = useMemo(
		() => groupMessages(messages.filter((m) => m.softDeletedAt == null)),
		[messages],
	);

	// Auto-grow + per-input overflow toggle. `height = auto` before
	// measuring so scrollHeight reflects the shrunk size on delete;
	// `overflowY` flips between 'hidden' and 'auto' per-keystroke so
	// short input never shows a scrollbar gutter.
	useEffect(() => {
		const el = composerRef.current;
		if (el == null) return;
		el.style.height = 'auto';
		const next = Math.min(el.scrollHeight, composerMaxHeight);
		el.style.height = `${next}px`;
		el.style.overflowY =
			el.scrollHeight > composerMaxHeight ? 'auto' : 'hidden';
	}, [composer, composerMaxHeight]);

	// Auto-scroll to bottom only when a NEW message arrives (id
	// monotonically greater than the last we scrolled for). Never on
	// history prepends at the top, and never on load-older.
	useEffect(() => {
		const el = scrollRef.current;
		const lastMessage = messages[messages.length - 1];
		if (el == null || lastMessage == null) return;
		const lastId = lastMessage.id;
		if (lastId > lastAutoScrollAt.current) {
			el.scrollTop = el.scrollHeight;
			lastAutoScrollAt.current = lastId;
		}
	}, [messages]);

	async function handleScroll(e: React.UIEvent<HTMLDivElement>): Promise<void> {
		if (onLoadOlder == null || loadingOlder === true || reachedStart === true) {
			return;
		}
		const el = e.currentTarget;
		if (el.scrollTop > 80) return;
		const prevHeight = el.scrollHeight;
		await onLoadOlder();
		// Preserve visual position: after prepend, scroll top = delta in height.
		requestAnimationFrame(() => {
			if (scrollRef.current != null) {
				const delta = scrollRef.current.scrollHeight - prevHeight;
				scrollRef.current.scrollTop = delta;
			}
		});
	}

	async function handleSend(): Promise<void> {
		const body = composer.trim();
		if (body === '' || composerDisabled === true) return;
		setComposer('');
		await onSend(body);
	}

	const typingLabel = useMemo(() => {
		if (typingNames == null || typingNames.length === 0) return null;
		if (typingNames.length === 1) return `${t('is typing…')}`;
		return t('Several people are typing…');
	}, [typingNames, t]);

	return (
		<div className="flex flex-1 flex-col overflow-hidden">
			<div
				ref={scrollRef}
				onScroll={(e) => void handleScroll(e)}
				className={`flex-1 overflow-y-auto ${compact ? 'px-2.5 py-2' : 'px-4 py-4'}`}
			>
				{banner != null && <div className="mb-2">{banner}</div>}
				{loadingOlder === true && (
					<div
						className="flex justify-center py-2 text-xs"
						style={{ color: colors.values.textTertiary }}
					>
						{t('Loading older messages…')}
					</div>
				)}
				{reachedStart === true && messages.length > 0 && (
					<div
						className="flex justify-center py-2 text-xs italic"
						style={{ color: colors.values.textTertiary }}
					>
						{t('Beginning of conversation')}
					</div>
				)}
				{groups.length === 0 ? (
					<EmptyThread compact={compact} />
				) : (
					groups.map((group) => (
						<MessageRow
							key={group.messages[0].uuid}
							group={group}
							participantsById={participantsById}
							isMine={
								(myWorkdUserId != null &&
									group.senderWorkdUserId === myWorkdUserId) ||
								(rightAlignedParticipantIds != null &&
									group.senderParticipantId != null &&
									rightAlignedParticipantIds.includes(
										group.senderParticipantId,
									))
							}
							showSenderInfoOnMine={showSenderInfoOnMine === true}
							compact={compact}
							sendErrors={sendErrors}
							onRetry={onRetry}
						/>
					))
				)}
			</div>

			{typingLabel != null && (
				<div
					className={`border-t text-xs italic ${compact ? 'px-2.5 py-0.5' : 'px-4 py-1'}`}
					style={{
						borderColor: colors.values.borderLight,
						color: colors.values.textTertiary,
					}}
				>
					{typingLabel}
				</div>
			)}

			{composerLeading != null && composerLeading}

			{composerHidden !== true && (
				<footer
					className={`flex items-end gap-0.5 border-t ${compact ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}
					style={{
						borderColor: colors.values.borderLight,
						backgroundColor: colors.values.background,
					}}
				>
					<textarea
						ref={composerRef}
						value={composer}
						onChange={(e) => setComposer(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								void handleSend();
							}
						}}
						rows={1}
						disabled={composerDisabled === true}
						placeholder={placeholder ?? t('Type a message…')}
						className="flex-1 resize-none bg-transparent px-1.5 py-1.5 text-sm leading-snug outline-none disabled:opacity-50"
						style={{
							color: colors.values.textPrimary,
							maxHeight: composerMaxHeight,
							overflowY: 'hidden',
						}}
					/>
					<button
						type="button"
						onClick={() => void handleSend()}
						disabled={composer.trim() === '' || composerDisabled === true}
						aria-label={t('Send')}
						className="ml-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40"
						style={{
							backgroundColor:
								composer.trim() === '' ? 'transparent' : colors.values.primary,
							color:
								composer.trim() === '' ? colors.values.textTertiary : '#FFFFFF',
						}}
					>
						<FontAwesomeIcon icon={faPaperPlane} className="h-3.5 w-3.5" />
					</button>
				</footer>
			)}

			{composerFooter != null && composerFooter}
		</div>
	);
};

// ------------------------- internals -------------------------

interface Group {
	/**
	 * Identity primitive — the sender's workdUserId, or null for system
	 * messages. Drives alignment ("is this mine?") and the visual
	 * grouping of consecutive bubbles from the same person.
	 */
	senderWorkdUserId: number | null;
	/**
	 * Conversation-scoped participant id. Carried solely so the renderer
	 * can look up the display name via `participantsById`. Null for
	 * system messages; briefly undefined on optimistic rows before the
	 * server fanout fills it in (the bubble still renders on the
	 * correct side because alignment uses workdUserId).
	 */
	senderParticipantId: number | null;
	kind: ChatMessageView['kind'];
	firstAt: Date;
	/** Non-empty: a group is only ever created with its first message. */
	messages: [ChatMessageView, ...Array<ChatMessageView>];
}

function groupMessages(messages: ReadonlyArray<ChatMessageView>): Array<Group> {
	const groups: Array<Group> = [];
	for (const m of messages) {
		const last = groups[groups.length - 1];
		const mAt = new Date(m.createdDate);
		const sameSender =
			last != null &&
			(last.kind === 'system') === (m.kind === 'system') &&
			last.senderWorkdUserId === (m.senderWorkdUserId ?? null);
		if (
			sameSender &&
			mAt.getTime() - last.firstAt.getTime() <= GROUP_WINDOW_MS
		) {
			last.messages.push(m);
		} else {
			groups.push({
				senderWorkdUserId: m.senderWorkdUserId ?? null,
				senderParticipantId: m.senderParticipantId ?? null,
				kind: m.kind,
				firstAt: mAt,
				messages: [m],
			});
		}
	}
	return groups;
}

const MessageRow: React.FC<{
	group: Group;
	participantsById: Map<number, ChatParticipantView>;
	isMine: boolean;
	showSenderInfoOnMine: boolean;
	compact: boolean;
	sendErrors?: Record<string, string>;
	onRetry?: (clientMessageId: string) => void;
}> = ({
	group,
	participantsById,
	isMine,
	showSenderInfoOnMine,
	compact,
	sendErrors,
	onRetry,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	// Kind is the authoritative source for system pills. Deriving from
	// `senderParticipantId == null` would misclassify optimistic rows
	// (which legitimately have no participant id yet) as system pills
	// — that was the original bug.
	const isSystem = group.kind === 'system';

	if (isSystem) {
		return (
			<div className="flex justify-center py-1.5">
				<div
					className="rounded-full px-3 py-1 text-xs"
					style={{
						backgroundColor: colors.values.surface,
						color: colors.values.textSecondary,
					}}
				>
					{/*
					 * NEVER translate message content. Chat messages are
					 * verbatim user-authored speech (or server-emitted
					 * system pills like "Connected with <Name>" /
					 * "Customer ended the chat" — also content the rep
					 * might quote/screenshot, which must read exactly as
					 * sent). Wrapping in t() would silently rewrite the
					 * sender's words. Render as-is.
					 */}
					{group.messages[0].content ?? ''}
				</div>
			</div>
		);
	}

	const participant =
		group.senderParticipantId != null
			? participantsById.get(group.senderParticipantId)
			: undefined;
	const senderName =
		participant?.displayName ??
		`${t('Participant')} ${group.senderParticipantId}`;
	const seed =
		participant?.userId != null
			? `u:${participant.userId}`
			: `p:${group.senderParticipantId}`;

	const bubbleBg = isMine
		? colors.values.primary
		: colors.values.surfaceElevated;
	const bubbleText = isMine ? '#FFFFFF' : colors.values.textPrimary;
	const bubbleBorder = isMine ? 'transparent' : colors.values.borderLight;

	// In an audit view (`showSenderInfoOnMine`) BOTH sides need the
	// sender name and avatar — there is no "current user" looking
	// at the transcript, so the live-chat convention of "mine has
	// no name because it's obviously you" doesn't apply. The
	// supervisor reading the archive needs to see both reps and
	// customers labeled.
	const showSenderInfo = !isMine || showSenderInfoOnMine;
	return (
		<div
			className={`mb-2 flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
		>
			{showSenderInfo && (
				<div className="pt-0.5">
					<Avatar seed={seed} name={senderName} size={compact ? 'sm' : 'md'} />
				</div>
			)}
			<div
				className={`flex min-w-0 flex-col ${isMine ? 'items-end' : 'items-start'} max-w-[78%]`}
			>
				{showSenderInfo && (
					<div className="mb-0.5 flex items-baseline gap-2">
						<span
							className="text-xs font-semibold"
							style={{ color: colors.values.textPrimary }}
						>
							{senderName}
						</span>
						<span
							className="text-[10px]"
							style={{ color: colors.values.textTertiary }}
						>
							{formatTime(group.firstAt)}
						</span>
					</div>
				)}

				<div className="space-y-1">
					{group.messages.map((m) => {
						const failure =
							m.clientMessageId != null && sendErrors != null
								? sendErrors[m.clientMessageId]
								: null;
						const isSending = m.id < 0 && failure == null;
						return (
							<div
								key={m.uuid}
								className={`group relative flex items-end gap-1.5 ${
									isMine ? 'flex-row-reverse' : 'flex-row'
								}`}
							>
								<div
									className={`whitespace-pre-wrap break-words rounded-2xl ${
										compact ? 'px-3 py-1.5 text-sm' : 'px-3.5 py-2 text-sm'
									} ${isMine ? 'rounded-br-md' : 'rounded-bl-md'}`}
									style={{
										backgroundColor: bubbleBg,
										color: bubbleText,
										border: `1px solid ${bubbleBorder}`,
										opacity: isSending ? 0.7 : 1,
									}}
								>
									{/*
									 * NEVER translate message content. Chat
									 * messages are verbatim user-authored
									 * speech and must render exactly as sent.
									 * Wrapping in t() would silently rewrite
									 * the sender's words and is a non-starter
									 * for a multi-language session where one
									 * party should see another's actual
									 * message, not a localized rewrite.
									 */}
									{m.content ?? ''}
									{m.editedAt != null && (
										<span
											className="ml-1.5 text-[10px]"
											style={{
												color: isMine
													? 'rgba(255,255,255,0.7)'
													: colors.values.textTertiary,
											}}
										>
											{t('(edited)')}
										</span>
									)}
								</div>
								{isMine && (
									<StatusIndicator
										isSending={isSending}
										failure={failure ?? null}
										onRetry={
											m.clientMessageId != null && onRetry != null
												? () => onRetry(m.clientMessageId!)
												: undefined
										}
									/>
								)}
							</div>
						);
					})}
				</div>

				{isMine && !showSenderInfoOnMine && (
					// Live-chat trailing timestamp under "mine" bubbles.
					// Suppressed in audit view because the timestamp
					// already appears next to the sender name above the
					// bubble (showSenderInfo path).
					<div
						className="mt-0.5 text-[10px]"
						style={{ color: colors.values.textTertiary }}
					>
						{formatTime(group.firstAt)}
					</div>
				)}
			</div>
		</div>
	);
};

const StatusIndicator: React.FC<{
	isSending: boolean;
	failure: string | null;
	onRetry?: () => void;
}> = ({ isSending, failure, onRetry }) => {
	const { t } = useTranslation();
	const colors = useThemeColors();

	if (failure != null) {
		return (
			<button
				type="button"
				onClick={onRetry}
				title={`${t('Failed to send — click to retry')}: ${failure}`}
				aria-label={t('Retry send')}
				className="flex h-5 w-5 items-center justify-center rounded-full"
				style={{ color: colors.values.danger }}
			>
				<FontAwesomeIcon icon={faCircleExclamation} className="h-3 w-3" />
			</button>
		);
	}
	if (isSending) {
		return (
			<div
				className="flex h-5 w-5 items-center justify-center"
				title={t('Sending…')}
			>
				<FontAwesomeIcon
					icon={faClock}
					className="h-2.5 w-2.5"
					style={{ color: colors.values.textTertiary }}
				/>
			</div>
		);
	}
	return (
		<div className="flex h-5 w-5 items-center justify-center" title={t('Sent')}>
			<FontAwesomeIcon
				icon={faCheck}
				className="h-2.5 w-2.5"
				style={{ color: colors.values.textTertiary }}
			/>
		</div>
	);
};

const EmptyThread: React.FC<{ compact: boolean }> = ({ compact }) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	return (
		<div
			className={`flex h-full flex-col items-center justify-center ${compact ? 'py-8' : 'py-16'}`}
		>
			<div
				className="mb-3 flex h-12 w-12 items-center justify-center rounded-full"
				style={{ backgroundColor: colors.values.surface }}
			>
				<FontAwesomeIcon
					icon={faPaperPlane}
					className="h-4 w-4"
					style={{ color: colors.values.textTertiary }}
				/>
			</div>
			<div
				className="text-sm font-medium"
				style={{ color: colors.values.textPrimary }}
			>
				{t('No messages yet')}
			</div>
			<div
				className="mt-1 text-xs"
				style={{ color: colors.values.textTertiary }}
			>
				{t('Be the first to send one.')}
			</div>
		</div>
	);
};

function formatTime(d: Date): string {
	return d.toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit',
	});
}
